import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import treatmentRecordService from '../services/TreatmentRecordService';
import customerService from '../services/CustomerService';
import practitionerService from '../services/PractitionerService';
import treatmentService from '../services/TreatmentService';
import WorkOrderService from '../services/WorkOrderService';
import appointmentService from '../services/AppointmentService';
import { Card, Button, Input, Select, Modal, SearchInput, CustomDropdown, Table, Alert } from '../components/ui';

/**
 * TreatmentRecordsPage component for viewing and managing treatment records
 * @returns {React.ReactElement} TreatmentRecordsPage component
 */
const TreatmentRecordsPage = () => {
  // Navigation
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();

  // State for treatment records
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for reference data
  const [customers, setCustomers] = useState([]);
  const [practitioners, setPractitioners] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [appointments, setAppointments] = useState([]);

  // State for search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [customerFilter, setCustomerFilter] = useState('all');
  const [practitionerFilter, setPractitionerFilter] = useState('all');
  const [treatmentFilter, setTreatmentFilter] = useState('all');
  const [followUpFilter, setFollowUpFilter] = useState('all');
  const [workOrderStatusFilter, setWorkOrderStatusFilter] = useState('all');
  const [appointmentFilter, setAppointmentFilter] = useState('all');

  // Check permissions
  const canViewRecords = hasPermission('treatmentRecords', 'read');
  const canManageRecords = hasPermission('treatmentRecords', 'write');

  // Fetch treatment records and reference data
  useEffect(() => {
    fetchRecordsAndReferenceData();
  }, []);

  // Function to fetch treatment records and reference data
  const fetchRecordsAndReferenceData = async () => {
    setLoading(true);
    try {
      // Fetch treatment records
      const recordsData = treatmentRecordService.getAllTreatmentRecords();

      // Fetch customers
      const customersData = customerService.getAllCustomers();
      setCustomers(customersData);

      // Fetch practitioners
      const practitionersData = practitionerService.getAllPractitioners();
      setPractitioners(practitionersData);

      // Fetch treatments
      const treatmentsData = treatmentService.getAllTreatments();
      setTreatments(treatmentsData);

      // Fetch appointments
      const appointmentsData = appointmentService.getAllAppointments();
      setAppointments(appointmentsData);

      // Enhance records with names, work order, and appointment info
      const enhancedRecords = recordsData.map(record => {
        const customer = customersData.find(c => c.id === record.customerId);
        const practitioner = practitionersData.find(p => p.id === record.practitionerId);
        const treatment = treatmentsData.find(t => t.id === record.treatmentId);
        const workOrder = record.workOrderId ? WorkOrderService.getWorkOrderById(record.workOrderId) : null;
        const appointment = record.appointmentId ? appointmentsData.find(a => a.id === record.appointmentId) : null;
        let workOrderStatus = '无工单';
        if (workOrder) {
          if (workOrder.status === 'completed') workOrderStatus = '已完成';
          else if (workOrder.status === 'in_progress') workOrderStatus = '进行中';
          else workOrderStatus = '待调理';
        }
        let settlementStatus = '未结算';
        if (record.settlement && record.settlement.paidAmount > 0) settlementStatus = '已结算';
        return {
          ...record,
          customerName: customer?.name || '未知客户',
          practitionerName: practitioner?.name || '未指定',
          treatmentName: treatment?.name || '未指定',
          workOrderStatus,
          appointmentTime: appointment?.appointmentTime || '',
          settlementStatus
        };
      });

      setRecords(enhancedRecords);
      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('获取调理记录失败');
    } finally {
      setLoading(false);
    }
  };

  // Filter records based on search and filters
  const filteredRecords = useMemo(() => {
    return records.filter(record => {
      // Search by customer name, treatment name, or summary
      const matchesSearch =
        record.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.treatmentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.summary?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        // Handle diagnosis as object or string
        (typeof record.diagnosis === 'string'
          ? record.diagnosis.toLowerCase().includes(searchTerm.toLowerCase())
          : record.diagnosis?.tcm?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          record.diagnosis?.western?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          record.diagnosis?.pattern?.toLowerCase().includes(searchTerm.toLowerCase()));

      // Filter by date range
      const recordDate = new Date(record.treatmentDate);
      const matchesDateRange =
        (!dateRange.startDate || recordDate >= new Date(dateRange.startDate)) &&
        (!dateRange.endDate || recordDate <= new Date(dateRange.endDate));

      // Filter by customer
      const matchesCustomer = customerFilter === 'all' || record.customerId === customerFilter;

      // Filter by practitioner
      const matchesPractitioner = practitionerFilter === 'all' || record.practitionerId === practitionerFilter;

      // Filter by treatment
      const matchesTreatment = treatmentFilter === 'all' || record.treatmentId === treatmentFilter;

      // Filter by follow-up status
      const matchesFollowUp = followUpFilter === 'all' ||
        (followUpFilter === 'followUp' && record.followUp) ||
        (followUpFilter === 'noFollowUp' && !record.followUp);

      return matchesSearch &&
        matchesDateRange &&
        matchesCustomer &&
        matchesPractitioner &&
        matchesTreatment &&
        matchesFollowUp;
    });
  }, [
    records,
    searchTerm,
    dateRange,
    customerFilter,
    practitionerFilter,
    treatmentFilter,
    followUpFilter
  ]);

  // Sort records by date (newest first)
  const sortedRecords = useMemo(() => {
    return [...filteredRecords].sort((a, b) => {
      return new Date(b.treatmentDate) - new Date(a.treatmentDate);
    });
  }, [filteredRecords]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '未记录';
    const date = new Date(dateString);

    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Format date for input
  const formatDateForInput = (date) => {
    if (!date) return '';
    return new Date(date).toISOString().split('T')[0];
  };

  // Handle date range change
  const handleDateRangeChange = (e, field) => {
    const value = e.target.value;
    setDateRange(prev => ({
      ...prev,
      [field]: value ? new Date(value).toISOString() : null
    }));
  };

  // Handle deleting a record
  const handleDeleteRecord = (recordId) => {
    if (window.confirm('确定要删除此调理记录吗？此操作无法撤销。')) {
      try {
        treatmentRecordService.deleteTreatmentRecord(recordId);
        fetchRecordsAndReferenceData();
      } catch (err) {
        console.error('Error deleting record:', err);
        alert('删除调理记录失败');
      }
    }
  };

  // 判断调理记录叫号状态
  function getCallStatus(workOrder, treatmentRecordId) {
    if (!workOrder || !workOrder.callHistory) return '未叫号';
    const calls = workOrder.callHistory.filter(
      h => h.treatmentRecordId === treatmentRecordId
    );
    if (calls.length === 0) return '未叫号';
    if (calls.length === 1) return '已叫号';
    if (calls.length > 1) return '再次叫号';
    return '未叫号';
  }

  // Table columns configuration
  const columns = [
    {
      key: 'treatment',
      title: '调理信息',
      render: (_, record) => {
        const workOrder = record.workOrderId ? WorkOrderService.getWorkOrderById(record.workOrderId) : null;
        const callStatus = getCallStatus(workOrder, record.id);
        let tagColor = 'bg-gray-300 text-gray-700';
        if (callStatus === '已叫号') tagColor = 'bg-emerald-100 text-emerald-700';
        if (callStatus === '再次叫号') tagColor = 'bg-yellow-100 text-yellow-700';
        return (
          <div className="flex flex-col">
            <div className="flex items-center space-x-2">
              <Link
                to={`/treatment-records/${record.id}`}
                className="text-sm font-medium text-gray-900 hover:text-emerald-600 transition-colors duration-200"
              >
                {record.treatmentProjectName || '未指定项目'}
              </Link>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${tagColor}`}>{callStatus}</span>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {formatDate(record.date)}
            </div>
          </div>
        );
      }
    },
    {
      key: 'customer',
      title: '客户信息',
      render: (_, record) => (
        <div>
          <Link
            to={`/customers/${record.customerId}`}
            className="text-sm font-medium text-emerald-600 hover:text-emerald-700 transition-colors duration-200"
          >
            {record.customerName}
          </Link>
        </div>
      )
    },
    {
      key: 'practitioner',
      title: '调理师',
      render: (_, record) => (
        <div className="text-sm text-gray-900">
          {record.practitionerName}
        </div>
      )
    },
    {
      key: 'progress',
      title: '调理进度',
      render: (_, record) => {
        // workOrderStatus: '无工单' | '已完成' | '进行中' | '待调理'
        let percent = 0;
        let color = 'bg-gray-300';
        if (record.workOrderStatus === '待调理') {
          percent = 0;
          color = 'bg-gray-300';
        } else if (record.workOrderStatus === '进行中') {
          percent = 50;
          color = 'bg-emerald-400';
        } else if (record.workOrderStatus === '已完成') {
          percent = 100;
          color = 'bg-emerald-600';
        } else if (record.workOrderStatus === '已取消') {
          percent = 0;
          color = 'bg-gray-400';
        }
        return (
          <div className="w-28 flex flex-col items-start">
            <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-3 rounded-full transition-all duration-300 ${color}`}
                style={{ width: `${percent}%` }}
              />
            </div>
            <span className="text-xs text-gray-500 mt-1">{record.workOrderStatus}</span>
          </div>
        );
      }
    },
    {
      key: 'actions',
      title: '操作',
      render: (_, record) => {
        const workOrder = record.workOrderId ? WorkOrderService.getWorkOrderById(record.workOrderId) : null;
        const callStatus = getCallStatus(workOrder, record.id);
        const handleCall = (type = 'call') => {
          if (!record.workOrderId) return;
          WorkOrderService.callNumber(record.workOrderId, {
            operatorId: user?.id || 'system',
            treatmentRecordId: record.id,
            type
          });
          fetchRecordsAndReferenceData();
        };
        return (
          <div className="flex items-center space-x-2">
            <Link
              to={`/treatment-records/${record.id}`}
              className="text-emerald-600 hover:text-emerald-700 text-sm font-medium transition-colors duration-200"
            >
              查看
            </Link>
            {canManageRecords && (
              <>
                <span className="text-gray-300">|</span>
                <button
                  onClick={() => handleDeleteRecord(record.id)}
                  className="text-red-600 hover:text-red-700 text-sm font-medium transition-colors duration-200"
                >
                  删除
                </button>
                <span className="text-gray-300">|</span>
                <button
                  onClick={() => handleCall(callStatus === '未叫号' ? 'call' : 'recall')}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors duration-200"
                  disabled={!record.workOrderId}
                >
                  {callStatus === '未叫号' ? '叫号' : '再次叫号'}
                </button>
              </>
            )}
          </div>
        );
      },
      cellClassName: 'text-right'
    }
  ];

  // Permission check - redirect if no view permission
  if (!canViewRecords) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-blue-50/30 flex items-center justify-center p-6">
        <div className="max-w-md w-full">
          <Card variant="simple" className="text-center py-12 bg-white/80 backdrop-blur-sm border-red-200/50">
            <div className="w-16 h-16 bg-gradient-to-br from-red-400 to-red-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v2m0-2h2m-2 0H10m4-6V9a2 2 0 00-2-2H8a2 2 0 00-2 2v6a2 2 0 002 2h4a2 2 0 002-2z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              权限不足
            </h2>
            <p className="text-gray-600 mb-6">
              您没有权限查看调理记录。请联系管理员获取相应权限。
            </p>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
            >
              返回首页
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-blue-50/30 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              调理记录
            </h1>
            <p className="text-gray-600">
              管理客户调理记录，跟踪治疗效果和健康状况
            </p>
          </div>
        </div>

        {/* Search and Filter */}
        <Card variant="simple" className="border-emerald-200/50 bg-white/70 backdrop-blur-sm">
          {/* 第一行：搜索框+时间筛选器 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <SearchInput
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="搜索客户名称、调理项目或记录内容..."
                className="w-full"
              />
            </div>
            <div>
              <div className="flex space-x-2">
                <Input
                  type="date"
                  value={dateRange.startDate ? formatDateForInput(dateRange.startDate) : ''}
                  onChange={(e) => handleDateRangeChange(e, 'startDate')}
                  placeholder="开始日期"
                />
                <span className="text-gray-500 self-center px-2">至</span>
                <Input
                  type="date"
                  value={dateRange.endDate ? formatDateForInput(dateRange.endDate) : ''}
                  onChange={(e) => handleDateRangeChange(e, 'endDate')}
                  placeholder="结束日期"
                />
              </div>
            </div>
          </div>
          {/* 第二行：客户、调理师、调理项目 */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-4">
            <div>
              <CustomDropdown
                options={[
                  { value: 'all', label: '全部客户' },
                  ...customers.map(customer => ({
                    value: customer.id,
                    label: customer.name
                  }))
                ]}
                value={customerFilter}
                onChange={(e) => setCustomerFilter(e.target.value)}
                placeholder="选择客户"
              />
            </div>
            <div>
              <CustomDropdown
                options={[
                  { value: 'all', label: '全部调理师' },
                  ...practitioners.map(practitioner => ({
                    value: practitioner.id,
                    label: practitioner.name
                  }))
                ]}
                value={practitionerFilter}
                onChange={(e) => setPractitionerFilter(e.target.value)}
                placeholder="选择调理师"
              />
            </div>
            <div>
              <CustomDropdown
                options={[
                  { value: 'all', label: '全部项目' },
                  ...treatments.map(treatment => ({
                    value: treatment.id,
                    label: treatment.name
                  }))
                ]}
                value={treatmentFilter}
                onChange={(e) => setTreatmentFilter(e.target.value)}
                placeholder="选择调理项目"
              />
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
            <span>共找到 {sortedRecords.length} 条调理记录</span>
            {searchTerm && (
              <span>搜索: "{searchTerm}"</span>
            )}
          </div>
        </Card>

        {/* Error Display */}
        {error && (
          <Alert variant="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Treatment Records Table */}
        <Table
          columns={columns}
          data={sortedRecords}
          loading={loading}
          variant="simple"
          className="border-emerald-200/50 bg-white/70 backdrop-blur-sm"
          emptyMessage="没有找到调理记录"
          emptyIcon={
            <svg className="h-16 w-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
          }
        />
      </div>
    </div>
  );
};

export default TreatmentRecordsPage;